import React, { useEffect, useRef, useState, useCallback, type ImgHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { ChevronDown } from '../icons/s2';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isSafeHttpUrl } from '../../utils/safeUrl';
import {
  Message,
  FeedbackCheckResponse,
  UserFeedbackSubmission,
  StreamingStep,
  ClarificationOption,
} from '../../types';
import {
  MessagesContainer,
  UserMessageContainer,
  UserMessageBubble,
  AssistantMessageContainer,
  AssistantMessageToneAnswered,
  AssistantMessageToneClarification,
  AssistantMessageToneUnanswered,
  AssistantMessageContent,
  AssistantMessageContentError,
  TypingIndicator,
  TypingDot,
  QualityScoreContainer,
  QualityScoreShimmer,
  QualityScoreBadge,
  QualityScoreBadgeClickable,
  QualityDot,
  QualityDotGood,
  QualityDotWarning,
  QualityDotPoor,
  QualityLabel,
  QualityNumeric,
  ReferencesContainer,
  ReferencesList,
} from './ChatBox.styles';
import { UserFeedbackModal } from './UserFeedbackModal';
import { JudgeFeedbackDialog } from './JudgeFeedbackDialog';

// Sparkle glyph that fronts the collapsible "Analysis" card (mirrors the
// generative-AI mark in the reference design).
const SparkleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
    <path d="M19 13.5l.85 2.4 2.4.85-2.4.85L19 20l-.85-2.4L15.75 16.75l2.4-.85L19 13.5z" />
  </svg>
);

function getVerdictDotClass(verdict: string): string {
  switch (verdict) {
    case 'good':
      return QualityDotGood;
    case 'needs_improvement':
      return QualityDotWarning;
    case 'poor':
      return QualityDotPoor;
    default:
      return QualityDotWarning;
  }
}

function getVerdictLabel(verdict: string, t: TFunction): string {
  switch (verdict) {
    case 'good':
      return t('chat.message.verdict.good');
    case 'needs_improvement':
      return t('chat.message.verdict.needsImprovement');
    case 'poor':
      return t('chat.message.verdict.poor');
    default:
      return verdict;
  }
}

const MarkdownImage: React.FC<ImgHTMLAttributes<HTMLImageElement>> = (props) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const alt = props.alt ?? '';

  if (status === 'error') return null;

  return (
    <img
      {...props}
      alt={alt}
      style={status === 'loading' ? { ...props.style, visibility: 'hidden', height: 0 } : props.style}
      onLoad={() => setStatus('loaded')}
      onError={() => setStatus('error')}
    />
  );
};

const REMARK_PLUGINS = [remarkGfm];

const markdownComponents: Components = {
  img: MarkdownImage,
};

function assistantAnswerToneClass(message: Message): string {
  if (message.isError) return '';
  if (message.clarification) return AssistantMessageToneClarification;
  if (message.answered === false) return AssistantMessageToneUnanswered;
  return AssistantMessageToneAnswered;
}

interface QualityScoreProps {
  qualityScore?: FeedbackCheckResponse | null;
  onClick?: () => void;
}

const QualityScore: React.FC<QualityScoreProps> = ({ qualityScore, onClick }) => {
  const { t } = useTranslation();
  // undefined = not requested or failed, show nothing
  if (qualityScore === undefined) return null;

  // null = loading, show shimmer
  if (qualityScore === null) {
    return (
      <div className={QualityScoreContainer}>
        <div className={QualityScoreShimmer} />
      </div>
    );
  }

  // Valid response, show clickable badge
  return (
    <div className={QualityScoreContainer}>
      <div
        className={`${QualityScoreBadge} ${QualityScoreBadgeClickable}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
        title={t('chat.message.qualityTitle')}
      >
        <span className={`${QualityDot} ${getVerdictDotClass(qualityScore.verdict)}`} />
        <span className={QualityLabel}>{getVerdictLabel(qualityScore.verdict, t)}</span>
        <span className={QualityNumeric}>{t('chat.message.scoreOutOf5', { score: qualityScore.quality_score.toFixed(1) })}</span>
      </div>
    </div>
  );
};

interface FeedbackButtonProps {
  hasFeedback: boolean;
  onClick: () => void;
}

const FeedbackButton: React.FC<FeedbackButtonProps> = ({ hasFeedback, onClick }) => {
  const { t } = useTranslation();
  return (
  <button
    className={`chat-rate-btn ${hasFeedback ? 'chat-rate-btn--submitted' : ''}`}
    onClick={onClick}
    aria-label={hasFeedback ? t('chat.message.editFeedback') : t('chat.message.giveFeedback')}
    title={hasFeedback ? t('chat.message.editFeedbackTitle') : t('chat.message.rateTitle')}
  >
    <svg width="15" height="15" viewBox="0 0 24 24" fill={hasFeedback ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
    <span>{hasFeedback ? t('chat.message.submitted') : t('chat.message.rate')}</span>
  </button>
  );
};

interface StreamingStepItemProps {
  step: StreamingStep;
  isLast: boolean;
}

const StreamingStepItem: React.FC<StreamingStepItemProps> = ({ step, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = step.details && Object.keys(step.details).length > 0;

  return (
    <li className="streaming-step">
      <div
        className={`streaming-step-header ${hasDetails ? 'streaming-step-header--expandable' : ''}`}
        onClick={hasDetails ? () => setExpanded(!expanded) : undefined}
        role={hasDetails ? 'button' : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        onKeyDown={hasDetails ? (e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); } : undefined}
      >
        <span className={`streaming-step-icon ${isLast ? 'streaming-step-icon--active' : ''}`} />
        <span className="streaming-step-label">{step.label}</span>
        {isLast && (
          <span className="streaming-step-dots">
            <span className="typing-dot" style={{ animationDelay: '0ms' }} />
            <span className="typing-dot" style={{ animationDelay: '150ms' }} />
            <span className="typing-dot" style={{ animationDelay: '300ms' }} />
          </span>
        )}
        {hasDetails && (
          <span className={`streaming-step-chevron ${expanded ? 'streaming-step-chevron--open' : ''}`}>&#9654;</span>
        )}
      </div>
      {expanded && hasDetails && (
        <pre className="streaming-step-details">{JSON.stringify(step.details, null, 2)}</pre>
      )}
    </li>
  );
};

interface AnalysisStepsAccordionProps {
  steps: StreamingStep[];
}

const AnalysisStepsAccordion: React.FC<AnalysisStepsAccordionProps> = ({ steps }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className={`chat-analysis-card ${open ? 'chat-analysis-card--open' : ''}`}>
      <button
        className="chat-analysis-header"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="chat-analysis-title">
          <SparkleIcon className="chat-analysis-sparkle" />
          <span>{t('chat.message.analysis')}</span>
        </span>
        <ChevronDown size="S" UNSAFE_className={`chat-analysis-chevron ${open ? 'chat-analysis-chevron--open' : ''}`} />
      </button>
      {open && (
        <div className="chat-analysis-body">
          <ul className="streaming-steps-list streaming-steps-list--collapsed">
            {steps.map((step) => (
              <StreamingStepItem key={step.id} step={step} isLast={false} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  siteId?: string;
  onSubmitFeedback?: (messageId: string, feedback: UserFeedbackSubmission) => void;
  /** Send a follow-up user message (e.g. from clarification chips). */
  onSendFollowUp?: (text: string) => void | Promise<void>;
  /** "Try asking" chips fill the composer instead of sending immediately. */
  onDraftSuggestedQuestion?: (text: string) => void;
  streamingSteps?: StreamingStep[];
  streamingContent?: string;
}

interface ClarificationFollowupsProps {
  clarification: boolean;
  answered: boolean | undefined;
  options: ClarificationOption[] | undefined;
  suggestions: string[] | undefined;
  isLoading: boolean;
  onSendFollowUp?: (text: string) => void | Promise<void>;
  onDraftSuggestedQuestion?: (text: string) => void;
}

const ClarificationFollowups: React.FC<ClarificationFollowupsProps> = ({
  clarification,
  answered,
  options,
  suggestions,
  isLoading,
  onSendFollowUp,
  onDraftSuggestedQuestion,
}) => {
  const { t } = useTranslation();
  const showOptions = clarification && options && options.length > 0;
  const showSuggestions = answered === false && suggestions && suggestions.length > 0;
  if (!showOptions && !showSuggestions) return null;

  return (
    <div className="chat-followups">
      {showOptions && (
        <>
          <div className="chat-followups-label">{t('chat.message.chooseOne')}</div>
          <div className="chat-clarification-option-grid" role="list">
            {options!.map((opt) => (
              <button
                key={opt.id || opt.title}
                type="button"
                className="chat-clarification-option-card"
                disabled={isLoading || !onSendFollowUp}
                onClick={() => void onSendFollowUp?.(opt.title)}
              >
                <span className="chat-clarification-option-title">{opt.title}</span>
                {opt.hint ? (
                  <span className="chat-clarification-option-hint">{opt.hint}</span>
                ) : null}
              </button>
            ))}
          </div>
        </>
      )}
      {showSuggestions && (
        <>
          <div className="chat-followups-label">{t('chat.message.tryAsking')}</div>
          <div className="chat-suggested-chip-row" role="list">
            {suggestions!.map((q) => (
              <button
                key={q}
                type="button"
                className="chat-suggested-chip"
                title={q}
                disabled={isLoading || (!onDraftSuggestedQuestion && !onSendFollowUp)}
                onClick={() => {
                  if (onDraftSuggestedQuestion) onDraftSuggestedQuestion(q);
                  else void onSendFollowUp?.(q);
                }}
              >
                <span className="chat-suggested-chip__text">{q}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  isLoading,
  siteId,
  onSubmitFeedback,
  onSendFollowUp,
  onDraftSuggestedQuestion,
  streamingSteps,
  streamingContent,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [feedbackModalMessageId, setFeedbackModalMessageId] = useState<string | null>(null);
  const [judgeDialogMessageId, setJudgeDialogMessageId] = useState<string | null>(null);
  // Per-message "Sources" disclosure (collapsed by default, like the design).
  const [openSources, setOpenSources] = useState<Set<string>>(new Set());
  const toggleSources = useCallback((id: string) => {
    setOpenSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  // Per-message "Why am I seeing this?" disclosure — same drawer behavior as Sources.
  const [openReasoning, setOpenReasoning] = useState<Set<string>>(new Set());
  const toggleReasoning = useCallback((id: string) => {
    setOpenReasoning((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Track whether the user has intentionally scrolled away from the bottom.
  // We flip this off on manual scroll-up and back on when they scroll near
  // the bottom again, so streaming never yanks them back.
  const userScrolledUp = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledUp.current = distanceFromBottom > 100;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll during streaming (token-by-token) and when new messages
  // arrive. Uses `instant` behavior so each frame lands at the true bottom
  // instead of queuing competing smooth animations.
  useEffect(() => {
    if (userScrolledUp.current) return;

    const container = containerRef.current;
    if (!container) return;

    const rafId = requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(rafId);
  }, [messages, isLoading, streamingContent, streamingSteps]);

  const handleOpenJudgeDialog = useCallback((messageId: string) => {
    setJudgeDialogMessageId(messageId);
  }, []);

  const handleCloseJudgeDialog = useCallback(() => {
    setJudgeDialogMessageId(null);
  }, []);

  const handleOpenFeedback = useCallback((messageId: string) => {
    setFeedbackModalMessageId(messageId);
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setFeedbackModalMessageId(null);
  }, []);

  const handleSubmitFeedback = useCallback((feedback: UserFeedbackSubmission) => {
    if (feedbackModalMessageId && onSubmitFeedback) {
      onSubmitFeedback(feedbackModalMessageId, feedback);
    }
    setFeedbackModalMessageId(null);
  }, [feedbackModalMessageId, onSubmitFeedback]);

  const feedbackModalMessage = feedbackModalMessageId
    ? messages.find((m) => m.id === feedbackModalMessageId)
    : null;

  const judgeDialogMessage = judgeDialogMessageId
    ? messages.find((m) => m.id === judgeDialogMessageId)
    : null;

  return (
    <div ref={containerRef} className={MessagesContainer} aria-live="polite" aria-label={t('chat.message.containerLabel')}>
      {messages.map((message) => {
        if (message.role === 'user') {
          return (
            <div key={message.id} className={UserMessageContainer}>
              <div className={UserMessageBubble}>{message.content}</div>
            </div>
          );
        }

        const canFeedback = !!(message.conversationId && message.turnNumber && onSubmitFeedback);
        const hasSources = !message.isError && !!message.finalDocumentList && message.finalDocumentList.length > 0;
        const sourcesOpen = openSources.has(message.id);
        const hasReasoning = !message.isError && !!message.answerReasoning;
        const reasoningOpen = openReasoning.has(message.id);
        const showFooter = !message.isError && (hasSources || hasReasoning || canFeedback || message.qualityScore !== undefined);

        return (
          <div
            key={message.id}
            className={`${AssistantMessageContainer} ${assistantAnswerToneClass(message)}`.trim()}
          >
            {message.analysisSteps && message.analysisSteps.length > 0 && (
              <AnalysisStepsAccordion steps={message.analysisSteps} />
            )}
            <div className="chat-response-card">
              <div
                className={`${AssistantMessageContent} ${message.isError ? AssistantMessageContentError : ''}`}
              >
                <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>{message.content}</ReactMarkdown>
              </div>
              {!message.isError && (
                <ClarificationFollowups
                  clarification={message.clarification === true}
                  answered={message.answered}
                  options={message.clarificationOptions}
                  suggestions={message.suggestedAlternativeQuestions}
                  isLoading={isLoading}
                  onSendFollowUp={onSendFollowUp}
                  onDraftSuggestedQuestion={onDraftSuggestedQuestion}
                />
              )}
              {hasReasoning && reasoningOpen && (
                <div className="chat-reasoning-drawer">{message.answerReasoning}</div>
              )}
              {hasSources && sourcesOpen && (
                <div className={ReferencesContainer}>
                  <ul className={ReferencesList}>
                    {message.finalDocumentList!.map((doc, idx) => (
                      <li key={idx}>
                        {isSafeHttpUrl(doc.published_url) ? (
                          <a href={doc.published_url} target="_blank" rel="noopener noreferrer">
                            {doc.title || doc.guid}
                          </a>
                        ) : (doc.source_site_id || siteId) && doc.guid ? (
                          <Link to={`/domains/${doc.source_site_id || siteId}/content?id=${encodeURIComponent(doc.guid)}`}>
                            {doc.title || doc.guid}
                          </Link>
                        ) : (
                          <span>{doc.title || doc.guid}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {showFooter && (
                <div className="chat-response-footer">
                  <div className="chat-response-footer-left">
                    {hasSources && (
                      <button
                        className={`chat-sources-btn ${sourcesOpen ? 'chat-sources-btn--open' : ''}`}
                        onClick={() => toggleSources(message.id)}
                        aria-expanded={sourcesOpen}
                      >
                        {message.answered === false ? t('chat.message.searchedSources') : t('chat.message.sources')}
                      </button>
                    )}
                    {hasReasoning && (
                      <button
                        className={`chat-sources-btn chat-reasoning-btn ${reasoningOpen ? 'chat-sources-btn--open' : ''}`}
                        onClick={() => toggleReasoning(message.id)}
                        aria-expanded={reasoningOpen}
                      >
                        <svg className="chat-reasoning-btn__icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M8 1.333A6.674 6.674 0 001.333 8 6.674 6.674 0 008 14.667 6.674 6.674 0 0014.667 8 6.674 6.674 0 008 1.333zm0 12A5.34 5.34 0 012.667 8 5.34 5.34 0 018 2.667 5.34 5.34 0 0113.333 8 5.34 5.34 0 018 13.333z" fill="currentColor" />
                          <path d="M7.333 7.333h1.334V11H7.333V7.333zM7.333 5h1.334v1.333H7.333V5z" fill="currentColor" />
                        </svg>
                        <span>{t('chat.message.whySeeingThis')}</span>
                      </button>
                    )}
                  </div>
                  <div className="chat-response-footer-right">
                    <QualityScore
                      qualityScore={message.qualityScore}
                      onClick={() => handleOpenJudgeDialog(message.id)}
                    />
                    {canFeedback && (
                      <FeedbackButton
                        hasFeedback={!!message.userFeedback}
                        onClick={() => handleOpenFeedback(message.id)}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      {isLoading && (
        <div className={AssistantMessageContainer}>
          {streamingSteps && streamingSteps.length > 0 && (
            <div className="chat-analysis-card chat-analysis-card--live chat-analysis-card--open">
              <div className="chat-analysis-header chat-analysis-header--static">
                <span className="chat-analysis-title">
                  <SparkleIcon className="chat-analysis-sparkle" />
                  <span>{t('chat.message.analysis')}</span>
                </span>
              </div>
              <div className="chat-analysis-body">
                <ul className="streaming-steps-list">
                  {streamingSteps.map((step, idx) => (
                    <StreamingStepItem
                      key={step.id}
                      step={step}
                      isLast={idx === streamingSteps.length - 1 && !streamingContent}
                    />
                  ))}
                </ul>
              </div>
            </div>
          )}
          {(streamingContent || !streamingSteps?.length) && (
            <div className="chat-response-card">
              <div className={AssistantMessageContent}>
                {streamingContent ? (
                  <>
                    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>{streamingContent}</ReactMarkdown>
                    <span className={TypingIndicator} style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4 }}>
                      <span className={TypingDot} style={{ animationDelay: '0ms' }} />
                      <span className={TypingDot} style={{ animationDelay: '150ms' }} />
                      <span className={TypingDot} style={{ animationDelay: '300ms' }} />
                    </span>
                  </>
                ) : (
                  <div className={TypingIndicator}>
                    <span className={TypingDot} style={{ animationDelay: '0ms' }} />
                    <span className={TypingDot} style={{ animationDelay: '150ms' }} />
                    <span className={TypingDot} style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {feedbackModalMessage && (
        <UserFeedbackModal
          onSubmit={handleSubmitFeedback}
          onClose={handleCloseFeedback}
          existingFeedback={feedbackModalMessage.userFeedback}
        />
      )}

      {judgeDialogMessage?.qualityScore && (
        <JudgeFeedbackDialog
          qualityScore={judgeDialogMessage.qualityScore}
          onClose={handleCloseJudgeDialog}
        />
      )}
    </div>
  );
};

export default MessageList;
